<template>
  <div class="email-confirm-wrapper">
    <div v-if="loading" class="email-confirm-loading">
      <a href="https://www.charidy.com" class="charidy-brand">
        <CdnLogoByLang />
      </a>
      <div class="email-confirm__message">
        {{ $t("email_confirm.checking_link", "Checking your verification link...") }}
      </div>
    </div>

    <div class="email-confirm" v-else-if="success">
      <div class="email-confirm__image">
        <img
          width="120"
          height="120"
          src="https://b313e8803f7a4150a884-6e0b076a1e92e31c40be44f466689c50.ssl.cf5.rackcdn.com/public_upload/1709060043_3738653632323166363339336431333536363831646233393866313463653664_31373039303630303433.png"
        />
      </div>

      <div class="email-confirm__result">
        {{ $t("email_confirm.email_verified", "Your email has been verified!") }}
      </div>

      <div class="email-confirm__action">
        <a href="https://charidy.com" class="bttn bttn--lg bttn--blue">
          {{ $t("email_confirm.home", "Home") }}
        </a>

        <a href="/" class="bttn bttn--lg bttn--orange">
          {{ $t("email_confirm.login", "Log in") }}
        </a>
      </div>
    </div>

    <div class="email-confirm-error" v-else-if="hasError">
      <a href="https://www.charidy.com" class="charidy-brand">
        <CdnLogoByLang />
      </a>
      <div class="email-confirm__result">
        <error-message :response="{}" :customMessage="errorMessage"></error-message>
      </div>

      <div class="email-confirm__action">
        <a href="/" class="bttn bttn--lg bttn--orange">
          {{ $t("email_confirm.proceed_to_login", "Proceed to Log in") }}
        </a>
      </div>
    </div>

    <div class="email-confirm-form" v-else>
      <a href="https://www.charidy.com" class="charidy-brand">
        <CdnLogoByLang />
      </a>
      <div class="email-confirm__result">
        {{ $t("email_confirm.set_password_title", "Set your password to activate the account") }}
      </div>
      <div class="email-confirm__message">
        {{
          $t(
            "email_confirm.set_password_subtitle",
            "Choose a secure password to finish setting up your Charidy account."
          )
        }}
      </div>

      <form class="email-confirm-form__form" @submit.prevent="submitPassword">
        <label class="email-confirm-form__label" for="password">
          {{ $t("email_confirm.new_password_label", "New password") }}
        </label>
        <input
          id="password"
          class="email-confirm-form__input"
          type="password"
          autocomplete="new-password"
          v-model.trim="form.password"
          :disabled="submitting"
          required
        />

        <label class="email-confirm-form__label" for="password-confirmation">
          {{ $t("email_confirm.confirm_password_label", "Confirm password") }}
        </label>
        <input
          id="password-confirmation"
          class="email-confirm-form__input"
          type="password"
          autocomplete="new-password"
          v-model.trim="form.passwordConfirmation"
          :disabled="submitting"
          required
        />

        <button
          class="bttn bttn--lg bttn--orange email-confirm-form__button"
          :disabled="submitting"
        >
          <span v-if="submitting">
            {{ $t("email_confirm.saving", "Saving...") }}
          </span>
          <span v-else>
            {{ $t("email_confirm.set_password_button", "Set password & continue") }}
          </span>
        </button>
      </form>

      <error-message :response="{}" :customMessage="errorMessage" :alert="true"></error-message>
    </div>
  </div>
</template>

<script>
import urls from "../constants/urls.js";
import axios from "axios";
import errorList from "@/components/error-message.vue";
import CdnLogoByLang from "@/common-components/cdn-v2/cdn-logo-by-lang.vue";

export default {
  name: "email-confirm",

  data: function () {
    return {
      success: false,
      loading: true,
      hasError: false,
      errorMessage: "",
      submitting: false,
      resetToken: "",
      form: {
        password: "",
        passwordConfirmation: "",
      },
    };
  },
  components: {
    errorMessage: errorList,
    CdnLogoByLang,
  },

  computed: {
    tokenVerify: function () {
      return this.$router.currentRoute.params.tokenVerify;
    },
  },

  methods: {
    checkToken() {
      this.loading = true;
      this.hasError = false;
      this.success = false;
      this.resetToken = "";
      this.errorMessage = "";

      axios
        .post(urls.checkToken.replace(":token", this.tokenVerify))
        .then((response) => {
          const data = (response && response.data) || {};
          const token = data.reset_token || "";
          const approved = data.approved === true;

          if (token) {
            this.resetToken = token;
            this.errorMessage = "";
            return;
          }

          if (approved) {
            this.success = true;
            return;
          }

          this.hasError = true;
          this.errorMessage = this.$t(
            "email_confirm.invalid_token",
            "The verification link is invalid or has expired."
          );
        })
        .catch((e) => {
          const response = (e && e.response) || null;
          const errorMessage = response?.data?.error
            ? String(response.data.error).toLowerCase()
            : "Unknown error occurred.";

          if (errorMessage.includes("account is already verified")) {
            this.success = true;
            return;
          }

          this.hasError = true;
          this.errorMessage = errorMessage;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    submitPassword: function () {
      if (this.submitting) {
        return;
      }

      if (!this.form.password || !this.form.passwordConfirmation) {
        this.errorMessage = this.$t("email_confirm.password_required", "Password is required.");
        return;
      }

      if (this.form.password !== this.form.passwordConfirmation) {
        this.errorMessage = this.$t(
          "email_confirm.passwords_do_not_match",
          "Passwords do not match."
        );
        return;
      }

      if (!this.resetToken) {
        this.errorMessage = this.$t(
          "email_confirm.invalid_token",
          "The verification link is invalid or has expired."
        );
        return;
      }

      this.submitting = true;

      axios
        .post(`${urls.resetPasswordToken}${this.resetToken}`, {
          password: this.form.password,
        })
        .then(() => {
          this.success = true;
        })
        .catch((e) => {
          const response = (e && e.response) || e;
          const errorMessage = response?.data?.error
            ? String(response.data.error).toLowerCase()
            : "Unknown error occurred.";
          this.errorMessage = errorMessage;
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },

  mounted() {
    this.checkToken();
  },
};
</script>

<style lang="scss" scoped>
.email-confirm-wrapper {
  width: 100%;
  min-height: 100vh;
  padding: 20px 15px;
  background-color: #fbfdff;
  display: flex;
  justify-content: center;
  align-items: center;
}

.email-confirm-loading {
  width: 100%;
  max-width: 400px;
  background-color: #ffffff;
  border: 1px solid #eaf4ff;
  border-radius: 6px;
  padding: 40px 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 30px 0 rgba(0, 0, 0, 0.1);
}

.email-confirm {
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;

  &__image {
    width: 120px;
    height: 120px;
    margin-bottom: 40px;
  }

  &__result {
    margin-bottom: 20px;
    font-family: "VitoExtended-Bold";
    font-size: 24px;
    text-align: center;
    color: #3cc7a0;
  }

  &__message {
    margin-bottom: 40px;
    font-family: "VitoExtended-Light";
    font-size: 18px;
    text-align: center;
  }

  &__action {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    .bttn {
      margin: 10px;
    }
    .bttn--app {
      background-color: #ffffff;
      color: #f26e66;
      border: 1px solid #f26e66;
    }
  }
}

.email-confirm-form {
  width: 100%;
  max-width: 400px;
  background-color: #ffffff;
  border: 1px solid #eaf4ff;
  border-radius: 6px;
  padding: 30px;
  box-shadow: 0 4px 30px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: stretch;

  .email-confirm__result {
    color: #1c3d5a;
  }

  .email-confirm__message {
    margin-bottom: 25px;
    text-align: center;
    color: #55647b;
  }
}

.email-confirm-form__form {
  display: flex;
  flex-direction: column;
}

.email-confirm-form__label {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #1c3d5a;
}

.email-confirm-form__input {
  height: 44px;
  padding: 0 12px;
  border: 1px solid #cdd9ed;
  border-radius: 6px;
  font-size: 16px;
  margin-bottom: 18px;
  transition: border-color 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: #3cc7a0;
    box-shadow: 0 0 0 2px rgba(60, 199, 160, 0.15);
  }
}

.email-confirm-form__button {
  width: 100%;
  justify-content: center;
}

.email-confirm-form__button[disabled] {
  opacity: 0.7;
  cursor: not-allowed;
}

.email-confirm-error {
  width: 100%;
  max-width: 568px;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #eaf4ff;
  padding: 20px;
  border-radius: 6px;
  background-color: #ffffff;
  box-shadow: 0 4px 30px 0 rgba(0, 0, 0, 0.1);
}
.charidy-brand {
  display: inline-block;
  width: 100%;
  text-align: center;
  margin-bottom: 20px;
  svg {
    display: block;
    width: 100%;
    height: 76px;
  }
}
</style>
